import type { ComponentType } from "react";
import type { ComponentMeta } from "@mijalowcode/types";
import { columnsRequestAdapter } from "../runtime-adapters/columns-request";
import { defaultComponentAdapterRegistry } from "../runtime-adapters";
import { listTemplateAdapter } from "../runtime-adapters/list-template";

type ComponentAdapter = {
  acceptsInternalRuntimeProps?: boolean;
  getResolveOptions?: (propPath: string[]) => {
    skipBindFallback?: boolean;
  };
  adaptProps: (
    props: Record<string, unknown>,
    context: {
      runRequestEvent: (
        requestEvent: string,
        payload: Record<string, unknown>,
      ) => Promise<unknown>;
    },
  ) => Record<string, unknown>;
};

export type MaterialRuntimeModule = {
  component: ComponentType<any>;
  adapter?: ComponentAdapter;
  meta?: ComponentMeta;
};

export type MaterialLoadOptions = {
  includeMeta?: boolean;
};

export type LoadMaterialModulesOptions = MaterialLoadOptions;

export type MaterialLoader = (
  options?: MaterialLoadOptions,
) => Promise<MaterialRuntimeModule>;

export type MaterialManifestEntry = MaterialLoader;

export type MaterialManifest = Record<string, MaterialManifestEntry>;

export type LoadedMaterialModules = {
  components: Record<string, ComponentType<any>>;
  adapters: Record<string, ComponentAdapter>;
  metas: Record<string, ComponentMeta>;
  missing: string[];
};

const defaultMaterialLoadOptions: MaterialLoadOptions = {
  includeMeta: false,
};

function shouldLoadMeta(options?: MaterialLoadOptions) {
  return Boolean(options?.includeMeta);
}

function pick(source: unknown, path: string): ComponentType<any> {
  return path.split(".").reduce<unknown>((acc, segment) => {
    if (!acc || typeof acc !== "object") return undefined;
    return (acc as Record<string, unknown>)[segment];
  }, source) as ComponentType<any>;
}

async function loadWeb(
  exportPath: string,
  metaLoader?: () => Promise<ComponentMeta>,
  options: MaterialLoadOptions = defaultMaterialLoadOptions,
): Promise<MaterialRuntimeModule> {
  const [web, meta] = await Promise.all([
    import("@mijadesign/web"),
    shouldLoadMeta(options) ? metaLoader?.() : undefined,
  ]);
  return { component: pick(web, exportPath), meta };
}

async function loadAntdLayout(
  exportPath: string,
  metaLoader?: () => Promise<ComponentMeta>,
  options: MaterialLoadOptions = defaultMaterialLoadOptions,
): Promise<MaterialRuntimeModule> {
  const [antd, meta] = await Promise.all([
    import("antd"),
    shouldLoadMeta(options) ? metaLoader?.() : undefined,
  ]);
  return { component: pick(antd, exportPath), meta };
}

async function loadDefault(
  componentLoader: () => Promise<{ default: ComponentType<any> }>,
  metaLoader?: () => Promise<ComponentMeta>,
  adapter?: ComponentAdapter,
  options: MaterialLoadOptions = defaultMaterialLoadOptions,
): Promise<MaterialRuntimeModule> {
  const [componentModule, meta] = await Promise.all([
    componentLoader(),
    shouldLoadMeta(options) ? metaLoader?.() : undefined,
  ]);
  return { component: componentModule.default, adapter, meta };
}

const loadColumnsAdapter = () => columnsRequestAdapter;

function mergeComponentAdapter(
  componentName: string,
  adapter?: ComponentAdapter,
): ComponentAdapter | undefined {
  const defaultAdapter = defaultComponentAdapterRegistry.get(componentName);
  if (!adapter) return defaultAdapter || undefined;
  if (!defaultAdapter) return adapter;
  return {
    ...defaultAdapter,
    ...adapter,
    acceptsInternalRuntimeProps:
      adapter.acceptsInternalRuntimeProps ??
      defaultAdapter.acceptsInternalRuntimeProps,
    getResolveOptions: (propPath) => ({
      ...(defaultAdapter.getResolveOptions?.(propPath) || {}),
      ...(adapter.getResolveOptions?.(propPath) || {}),
    }),
  };
}

const manifestLoadCache = new WeakMap<
  MaterialManifest,
  Map<string, Promise<MaterialRuntimeModule>>
>();

function getManifestCache(manifest: MaterialManifest) {
  const existing = manifestLoadCache.get(manifest);
  if (existing) return existing;
  const next = new Map<string, Promise<MaterialRuntimeModule>>();
  manifestLoadCache.set(manifest, next);
  return next;
}

function loadCachedMaterial(
  componentName: string,
  entry: MaterialManifestEntry,
  manifest: MaterialManifest,
  options: MaterialLoadOptions,
) {
  const cache = getManifestCache(manifest);
  const cacheKey = `${componentName}:${shouldLoadMeta(options) ? "meta" : "runtime"}`;
  const cached = cache.get(cacheKey);
  if (cached) return cached;
  const loading = entry(options).catch((error: unknown) => {
    cache.delete(cacheKey);
    throw error;
  });
  cache.set(cacheKey, loading);
  return loading;
}

export function clearMaterialLoadCache(manifest?: MaterialManifest) {
  if (manifest) {
    manifestLoadCache.delete(manifest);
    return;
  }
  manifestLoadCache.delete(webMaterialManifest);
}

export const webMaterialManifest: MaterialManifest = {
  Button: () =>
    loadDefault(
      () => import("../antd/Button/ButtonWrapper"),
      async () => (await import("../antd/Button/meta")).ButtonMeta,
    ),
  Space: () =>
    loadDefault(
      () => import("../antd/Space/SpaceWrapper"),
      async () => (await import("../antd/Space/meta")).SpaceMeta,
    ),
  DesensitizationDisplay: () =>
    loadDefault(
      () => import("../custom/DesensitizationDisplay"),
      async () =>
        (await import("../custom/DesensitizationDisplay/meta"))
          .DesensitizationDisplayMeta,
    ),

  Layout: () =>
    loadAntdLayout("Layout", async () =>
      (await import("../antd/Layout/meta")).LayoutMeta
    ),
  "Layout.Header": () => loadAntdLayout("Layout.Header"),
  "Layout.Sider": () => loadAntdLayout("Layout.Sider"),
  "Layout.Content": () => loadAntdLayout("Layout.Content"),
  "Layout.Footer": () => loadAntdLayout("Layout.Footer"),
  Header: () =>
    loadAntdLayout("Layout.Header", async () =>
      (await import("../antd/Layout/meta")).HeaderMeta
    ),
  Sider: () =>
    loadAntdLayout("Layout.Sider", async () =>
      (await import("../antd/Layout/meta")).SiderMeta
    ),
  Content: () =>
    loadAntdLayout("Layout.Content", async () =>
      (await import("../antd/Layout/meta")).ContentMeta
    ),
  Footer: () =>
    loadAntdLayout("Layout.Footer", async () =>
      (await import("../antd/Layout/meta")).FooterMeta
    ),
  Row: () =>
    loadWeb("Row", async () => (await import("../antd/Layout/meta")).RowMeta),
  Col: () =>
    loadWeb("Col", async () => (await import("../antd/Layout/meta")).ColMeta),
  Card: () =>
    loadWeb("Card", async () => (await import("../antd/Card/meta")).CardMeta),

  Input: () =>
    loadWeb("Input", async () => (await import("../antd/Input/meta")).InputMeta),
  "Input.TextArea": () =>
    loadWeb("Input.TextArea", async () =>
      (await import("../antd/Input/meta")).InputTextAreaMeta
    ),
  "Input.Search": () => loadWeb("Input.Search"),
  "Input.Password": () => loadWeb("Input.Password"),
  Form: () =>
    loadWeb("Form", async () => (await import("../antd/Form/meta")).FormMeta),
  "Form.Item": () =>
    loadWeb("Form.Item", async () =>
      (await import("../antd/Form/meta")).FormItemMeta
    ),
  "Form.List": () =>
    loadWeb("Form.List", async () =>
      (await import("../antd/Form/meta")).FormListMeta
    ),
  Select: () =>
    loadWeb("Select", async () =>
      (await import("../antd/Select/meta")).SelectMeta
    ),
  InputNumber: () =>
    loadWeb("InputNumber", async () =>
      (await import("../antd/InputNumber/meta")).InputNumberMeta
    ),

  DatePicker: () =>
    loadWeb("DatePicker", async () =>
      (await import("../antd/DatePicker/meta")).DatePickerMeta
    ),
  "DatePicker.RangePicker": () =>
    loadWeb("DatePicker.RangePicker", async () =>
      (await import("../antd/DatePicker/meta")).RangePickerMeta
    ),
  TimePicker: () =>
    loadWeb("TimePicker", async () =>
      (await import("../antd/DatePicker/meta")).TimePickerMeta
    ),
  Upload: () =>
    loadWeb("Upload", async () =>
      (await import("../antd/Upload/meta")).UploadMeta
    ),
  Tree: () =>
    loadWeb("Tree", async () => (await import("../antd/Tree/meta")).TreeMeta),
  TreeSelect: () =>
    loadWeb("TreeSelect", async () =>
      (await import("../antd/Tree/meta")).TreeSelectMeta
    ),
  Tabs: () =>
    loadWeb("Tabs", async () => (await import("../antd/Tabs/meta")).TabsMeta),
  "Tabs.TabPane": () =>
    loadWeb("Tabs.TabPane", async () =>
      (await import("../antd/Tabs/meta")).TabPaneMeta
    ),
  Steps: () =>
    loadWeb("Steps", async () => (await import("../antd/Steps/meta")).StepsMeta),
  "Steps.Step": () =>
    loadWeb("Steps.Step", async () =>
      (await import("../antd/Steps/meta")).StepMeta
    ),
  Tag: () =>
    loadDefault(
      () => import("../antd/Tag/TagWrapper"),
      async () => (await import("../antd/Tag/meta")).TagMeta,
    ),
  "Tag.CheckableTag": () =>
    loadWeb("Tag.CheckableTag", async () =>
      (await import("../antd/Tag/meta")).CheckableTagMeta
    ),
  ScenarioFlowBoard: () =>
    loadDefault(
      () => import("../custom/scenario/ScenarioFlowBoard"),
      async () =>
        (await import("../custom/scenario/ScenarioFlowBoard/meta"))
          .ScenarioFlowBoardMeta,
    ),
  ScenarioFlowScene: () =>
    loadDefault(
      () => import("../custom/scenario/ScenarioFlowScene"),
      async () =>
        (await import("../custom/scenario/ScenarioFlowScene/meta"))
          .ScenarioFlowSceneMeta,
    ),
  ScenarioFlowStep: () =>
    loadDefault(
      () => import("../custom/scenario/ScenarioFlowStep"),
      async () =>
        (await import("../custom/scenario/ScenarioFlowStep/meta"))
          .ScenarioFlowStepMeta,
    ),

  Badge: () =>
    loadWeb("Badge", async () => (await import("../antd/Badge/meta")).BadgeMeta),
  "Badge.Ribbon": () =>
    loadWeb("Badge.Ribbon", async () =>
      (await import("../antd/Badge/meta")).BadgeRibbonMeta
    ),
  Avatar: () =>
    loadWeb("Avatar", async () =>
      (await import("../antd/Avatar/meta")).AvatarMeta
    ),
  "Avatar.Group": () =>
    loadWeb("Avatar.Group", async () =>
      (await import("../antd/Avatar/meta")).AvatarGroupMeta
    ),
  Dropdown: () =>
    loadWeb("Dropdown", async () =>
      (await import("../antd/Dropdown/meta")).DropdownMeta
    ),
  Breadcrumb: () =>
    loadDefault(
      () => import("../antd/Breadcrumb/BreadcrumbWrapper"),
      async () => (await import("../antd/Breadcrumb/meta")).BreadcrumbMeta,
    ),
  "Dropdown.Button": () =>
    loadWeb("Dropdown.Button", async () =>
      (await import("../antd/Dropdown/meta")).DropdownButtonMeta
    ),
  Pagination: () =>
    loadWeb("Pagination", async () =>
      (await import("../antd/Pagination/meta")).PaginationMeta
    ),
  Switch: () =>
    loadWeb("Switch", async () =>
      (await import("../antd/Switch/meta")).SwitchMeta
    ),
  Radio: () =>
    loadWeb("Radio", async () => (await import("../antd/Radio/meta")).RadioMeta),
  "Radio.Group": () =>
    loadWeb("Radio.Group", async () =>
      (await import("../antd/Radio/meta")).RadioGroupMeta
    ),
  RadioButton: () => loadWeb("Radio.Button"),
  Checkbox: () =>
    loadWeb("Checkbox", async () =>
      (await import("../antd/Checkbox/meta")).CheckboxMeta
    ),
  "Checkbox.Group": () =>
    loadWeb("Checkbox.Group", async () =>
      (await import("../antd/Checkbox/meta")).CheckboxGroupMeta
    ),
  Cascader: () =>
    loadWeb("Cascader", async () =>
      (await import("../antd/Cascader/meta")).CascaderMeta
    ),
  Table: () =>
    loadWeb(
      "Table",
      async () => (await import("../antd/Table/meta")).TableMeta,
    ).then((module) => ({ ...module, adapter: loadColumnsAdapter() })),

  Typography: () => loadWeb("Typography"),
  "Typography.Title": () =>
    loadWeb("Typography.Title", async () =>
      (await import("../antd/Typography/meta")).TypographyTitleMeta
    ),
  "Typography.Paragraph": () =>
    loadWeb("Typography.Paragraph", async () =>
      (await import("../antd/Typography/meta")).TypographyParagraphMeta
    ),
  "Typography.Text": () =>
    loadWeb("Typography.Text", async () =>
      (await import("../antd/Typography/meta")).TypographyTextMeta
    ),

  ProTable: () =>
    loadDefault(
      () => import("../antdPro/ProTable/ProTableWrapper"),
      async () => (await import("../antdPro/ProTable/meta")).ProTableMeta,
      loadColumnsAdapter(),
    ),
  SchemaForm: () =>
    loadDefault(
      () => import("../antdPro/SchemaForm/SchemaFormWrapper"),
      async () => (await import("../antdPro/SchemaForm/meta")).SchemaFormMeta,
      loadColumnsAdapter(),
    ),
  FilterColumns: () =>
    loadDefault(
      () => import("../antdPro/FilterColumns/FilterColumnsWrapper"),
      async () =>
        (await import("../antdPro/FilterColumns/meta")).FilterColumnsMeta,
    ),

  Modal: () =>
    loadDefault(
      () => import("../antd/Modal/ModalWrapper"),
      async () => (await import("../antd/Modal/meta")).ModalMeta,
    ),
  Drawer: () =>
    loadDefault(
      () => import("../antd/Drawer/DrawerWrapper"),
      async () => (await import("../antd/Drawer/meta")).DrawerMeta,
    ),
  Descriptions: () =>
    loadDefault(
      () => import("../antd/Descriptions/DescriptionsWrapper"),
      async () => (await import("../antd/Descriptions/meta")).DescriptionsMeta,
    ),
  "Descriptions.Item": () => loadWeb("Descriptions.Item"),
  List: () =>
    loadDefault(
      () => import("../antd/List/ListWrapper"),
      async () => (await import("../antd/List/meta")).ListMeta,
      listTemplateAdapter,
    ),
  "List.Item": () =>
    loadWeb("List.Item", async () =>
      (await import("../antd/List/meta")).ListItemMeta
    ),
  Empty: () =>
    loadDefault(
      () => import("../antd/Empty/EmptyWrapper"),
      async () => (await import("../antd/Empty/meta")).EmptyMeta,
    ),
  Spin: () =>
    loadWeb("Spin", async () => (await import("../antd/Spin/meta")).SpinMeta),
  Alert: () =>
    loadWeb("Alert", async () => (await import("../antd/Alert/meta")).AlertMeta),
  Progress: () =>
    loadWeb("Progress", async () =>
      (await import("../antd/Progress/meta")).ProgressMeta
    ),
  Skeleton: () =>
    loadWeb("Skeleton", async () =>
      (await import("../antd/Tooltip/meta")).SkeletonMeta
    ),
  Tooltip: () =>
    loadWeb("Tooltip", async () =>
      (await import("../antd/Tooltip/meta")).TooltipMeta
    ),
  Popconfirm: () =>
    loadWeb("Popconfirm", async () =>
      (await import("../antd/Tooltip/meta")).PopconfirmMeta
    ),
  Popover: () =>
    loadDefault(
      () => import("../antd/Popover/PopoverWrapper"),
      async () => (await import("../antd/Popover/meta")).PopoverMeta
    ),
  Divider: () =>
    loadWeb("Divider", async () =>
      (await import("../antd/Divider/meta")).DividerMeta
    ),
  Statistic: () =>
    loadWeb("Statistic", async () =>
      (await import("../antd/Statistic/meta")).StatisticMeta
    ),
  Timeline: () =>
    loadWeb("Timeline", async () =>
      (await import("../antd/Timeline/meta")).TimelineMeta
    ),
  "Timeline.Item": () =>
    loadWeb("Timeline.Item", async () =>
      (await import("../antd/Timeline/meta")).TimelineItemMeta
    ),
  Result: () =>
    loadWeb("Result", async () => (await import("../antd/Result/meta")).ResultMeta),
};

export async function loadMaterialModules(
  componentNames: Iterable<string>,
  manifest: MaterialManifest = webMaterialManifest,
  options: LoadMaterialModulesOptions = defaultMaterialLoadOptions,
): Promise<LoadedMaterialModules> {
  const components: Record<string, ComponentType<any>> = {};
  const adapters: Record<string, ComponentAdapter> = {};
  const metas: Record<string, ComponentMeta> = {};
  const missing: string[] = [];
  const names = Array.from(new Set(Array.from(componentNames).filter(Boolean)));
  const pending = [...names];
  const visited = new Set<string>();

  while (pending.length > 0) {
    const componentName = pending.shift();
    if (!componentName || visited.has(componentName)) continue;
    visited.add(componentName);

    const entry = manifest[componentName];
    if (!entry) {
      missing.push(componentName);
      continue;
    }

    // 运行时只加载 schema 实际收集到的物料，避免复杂物料的历史显式依赖拉入未使用组件。
  }

  await Promise.all(
    Array.from(visited).map(async (componentName) => {
      const loader = manifest[componentName];
      if (!loader) {
        return;
      }
      const module = await loadCachedMaterial(
        componentName,
        loader,
        manifest,
        options,
      );
      if (!module.component) {
        missing.push(componentName);
        return;
      }
      components[componentName] = module.component;
      const adapter = mergeComponentAdapter(componentName, module.adapter);
      if (adapter) adapters[componentName] = adapter;
      if (module.meta) metas[componentName] = module.meta;
    }),
  );

  return { components, adapters, metas, missing };
}
